import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import styles from './CollectibleCoinViewer.module.css';

const FACE_FRONT = 'front';
const FACE_BACK = 'back';
const FLIP_DURATION_MS = 900;
const REDUCED_FLIP_DURATION_MS = 240;

function easeInOutCubic(value) {
  if (value < 0.5) return 4 * value * value * value;
  return 1 - ((-2 * value + 2) ** 3) / 2;
}

export default function CollectibleCoinViewer({
  playbackUrl = '',
  posterUrl = '',
  backTextureUrl = '',
  title = 'Collectible video',
  className = '',
  onPlaybackError = null,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const coinGroupRef = useRef(null);
  const frameRef = useRef(0);
  const resizeObserverRef = useRef(null);
  const videoRef = useRef(null);
  const frontMaterialRef = useRef(null);
  const backMaterialRef = useRef(null);
  const posterTextureRef = useRef(null);
  const videoTextureRef = useRef(null);
  const backTextureRef = useRef(null);
  const edgeGeometryRef = useRef(null);
  const faceGeometryRef = useRef(null);
  const startedRef = useRef(false);
  const faceRef = useRef(FACE_FRONT);
  const interactionStepRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const flipStateRef = useRef({
    active: false,
    startTime: 0,
    from: 0,
    to: 0,
  });
  const onPlaybackErrorRef = useRef(onPlaybackError);

  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
  const [currentFace, setCurrentFace] = useState(FACE_FRONT);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const accessibilityLabel = useMemo(() => {
    if (!hasStartedPlayback) return `Play ${title} on 3D coin`;
    if (isMuted) return `Unmute and flip ${title} coin`;
    if (currentFace === FACE_BACK) return `Flip ${title} coin to front`;
    return `Flip ${title} coin to back`;
  }, [currentFace, hasStartedPlayback, isMuted, title]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = () => {
      const nextValue = Boolean(mediaQuery.matches);
      reducedMotionRef.current = nextValue;
      setPrefersReducedMotion(nextValue);
    };

    syncPreference();
    mediaQuery.addEventListener('change', syncPreference);
    return () => mediaQuery.removeEventListener('change', syncPreference);
  }, []);

  useEffect(() => {
    startedRef.current = hasStartedPlayback;
  }, [hasStartedPlayback]);

  useEffect(() => {
    faceRef.current = currentFace;
  }, [currentFace]);

  useEffect(() => {
    onPlaybackErrorRef.current = onPlaybackError;
  }, [onPlaybackError]);

  const reportPlaybackError = (error, { ignoreInterrupted = false } = {}) => {
    const message = String(error?.message || '').trim();
    const name = String(error?.name || '').trim();
    if (!message && !name) return;
    if (name === 'AbortError') return;
    if (ignoreInterrupted && message.toLowerCase().includes('interrupted by a call to pause')) return;
    onPlaybackErrorRef.current?.(error);
  };

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current || !playbackUrl) return undefined;

    let disposed = false;
    setHasStartedPlayback(false);
    setCurrentFace(FACE_FRONT);
    setIsFlipping(false);
    setIsReady(false);
    setIsMuted(true);
    startedRef.current = false;
    faceRef.current = FACE_FRONT;
    interactionStepRef.current = 0;
    flipStateRef.current = { active: false, startTime: 0, from: 0, to: 0 };

    const canvas = canvasRef.current;
    const container = containerRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(24, 1, 0.1, 100);
    camera.position.set(0, 0.02, 4.2);

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xfff4d7, 2.35);
    keyLight.position.set(2.3, 2.8, 3.4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.9);
    fillLight.position.set(-2.6, -0.2, 2.2);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 1.25);
    rimLight.position.set(0.3, 3.2, -2.4);
    scene.add(rimLight);

    const coinGroup = new THREE.Group();
    coinGroup.position.set(0, 0, 0);
    coinGroup.scale.setScalar(0.82);
    scene.add(coinGroup);
    coinGroupRef.current = coinGroup;

    const radius = 1.05;
    const depth = 0.18;
    const faceOffset = depth / 2 + 0.002;
    const faceGeometry = new THREE.CircleGeometry(radius, 96);
    const edgeGeometry = new THREE.CylinderGeometry(radius, radius, depth, 96, 1, false);
    edgeGeometry.rotateX(Math.PI / 2);
    faceGeometryRef.current = faceGeometry;
    edgeGeometryRef.current = edgeGeometry;

    const frontMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.18,
      roughness: 0.72,
    });
    const backMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#d7dbe2'),
      metalness: 0.92,
      roughness: 0.34,
      emissive: new THREE.Color('#151922'),
      emissiveIntensity: 0.06,
    });
    const edgeMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#b88939'),
      metalness: 0.95,
      roughness: 0.26,
    });
    frontMaterialRef.current = frontMaterial;
    backMaterialRef.current = backMaterial;

    const frontFace = new THREE.Mesh(faceGeometry, frontMaterial);
    frontFace.position.z = faceOffset;
    coinGroup.add(frontFace);

    const backFace = new THREE.Mesh(faceGeometry, backMaterial);
    backFace.position.z = -faceOffset;
    backFace.rotation.y = Math.PI;
    coinGroup.add(backFace);

    const edge = new THREE.Mesh(edgeGeometry, edgeMaterial);
    coinGroup.add(edge);

    const ringGeometry = new THREE.TorusGeometry(radius * 0.96, 0.04, 24, 120);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#f3d58a'),
      metalness: 0.97,
      roughness: 0.23,
    });
    const frontRing = new THREE.Mesh(ringGeometry, ringMaterial);
    frontRing.position.z = faceOffset - 0.01;
    coinGroup.add(frontRing);

    const backRing = new THREE.Mesh(ringGeometry, ringMaterial.clone());
    backRing.position.z = -faceOffset + 0.01;
    coinGroup.add(backRing);

    if (posterUrl) {
      const loader = new THREE.TextureLoader();
      loader.load(
        posterUrl,
        (texture) => {
          if (disposed) {
            texture.dispose();
            return;
          }
          texture.colorSpace = THREE.SRGBColorSpace;
          posterTextureRef.current = texture;
          frontMaterial.map = texture;
          frontMaterial.needsUpdate = true;
          setIsReady(true);
        },
        undefined,
        () => setIsReady(true)
      );
    } else {
      setIsReady(true);
    }

    if (backTextureUrl) {
      const loader = new THREE.TextureLoader();
      loader.load(
        backTextureUrl,
        (texture) => {
          if (disposed) {
            texture.dispose();
            return;
          }
          texture.colorSpace = THREE.SRGBColorSpace;
          backTextureRef.current = texture;
          backMaterial.map = texture;
          backMaterial.needsUpdate = true;
        },
        undefined,
        () => {}
      );
    }

    const video = document.createElement('video');
    video.src = playbackUrl;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.disablePictureInPicture = true;
    video.controls = false;
    videoRef.current = video;

    const attemptAutoplay = async () => {
      try {
        await video.play();
        if (disposed) return;
        if (!frontMaterialRef.current) return;
        if (!videoTextureRef.current) {
          const videoTexture = new THREE.VideoTexture(video);
          videoTexture.colorSpace = THREE.SRGBColorSpace;
          videoTexture.generateMipmaps = false;
          videoTexture.minFilter = THREE.LinearFilter;
          videoTexture.magFilter = THREE.LinearFilter;
          videoTextureRef.current = videoTexture;
        }
        frontMaterialRef.current.map = videoTextureRef.current;
        frontMaterialRef.current.needsUpdate = true;
        interactionStepRef.current = 0;
        startedRef.current = true;
        setHasStartedPlayback(true);
        setCurrentFace(FACE_FRONT);
        setIsMuted(true);
      } catch (error) {
        if (disposed) return;
        reportPlaybackError(error, { ignoreInterrupted: true });
      }
    };

    const updateRendererSize = () => {
      if (!container || !rendererRef.current || !cameraRef.current) return;
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      rendererRef.current.setSize(width, height, false);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    };

    updateRendererSize();
    resizeObserverRef.current = new ResizeObserver(updateRendererSize);
    resizeObserverRef.current.observe(container);
    window.addEventListener('resize', updateRendererSize);

    const renderFrame = (timestamp) => {
      if (disposed || !coinGroupRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current) return;

      const coin = coinGroupRef.current;
      const flipState = flipStateRef.current;
      const reducedMotion = reducedMotionRef.current;
      if (flipState.active) {
        const duration = reducedMotion ? REDUCED_FLIP_DURATION_MS : FLIP_DURATION_MS;
        const progress = Math.min((timestamp - flipState.startTime) / duration, 1);
        const eased = easeInOutCubic(progress);
        coin.rotation.y = flipState.from + ((flipState.to - flipState.from) * eased);
        coin.rotation.x = reducedMotion ? 0.025 : 0.045;
        coin.rotation.z = 0;
        coin.position.y = 0;
        if (progress >= 1) {
          flipState.active = false;
          coin.rotation.y = flipState.to;
          setIsFlipping(false);
        }
      } else {
        const idleTime = timestamp * 0.001;
        coin.rotation.y = faceRef.current === FACE_FRONT ? 0 : Math.PI;
        if (reducedMotion) {
          coin.rotation.x = 0.025;
          coin.rotation.z = 0;
          coin.position.y = 0;
        } else {
          coin.rotation.x = 0.045 + (Math.sin(idleTime * 0.9) * 0.018);
          coin.rotation.z = Math.sin(idleTime * 0.65) * 0.012;
          coin.position.y = Math.sin(idleTime * 1.1) * 0.01;
        }
      }

      rendererRef.current.render(sceneRef.current, cameraRef.current);
      frameRef.current = window.requestAnimationFrame(renderFrame);
    };

    frameRef.current = window.requestAnimationFrame(renderFrame);
    // Warm the video element early so the first user interaction can start
    // from a ready state, but don't surface benign interrupted play() errors.
    attemptAutoplay();

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', updateRendererSize);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
        videoRef.current = null;
      }

      if (videoTextureRef.current) {
        videoTextureRef.current.dispose();
        videoTextureRef.current = null;
      }
      if (posterTextureRef.current) {
        posterTextureRef.current.dispose();
        posterTextureRef.current = null;
      }
      if (backTextureRef.current) {
        backTextureRef.current.dispose();
        backTextureRef.current = null;
      }

      frontMaterial.dispose();
      backMaterial.dispose();
      edgeMaterial.dispose();
      ringMaterial.dispose();
      backRing.material.dispose();
      edgeGeometry.dispose();
      faceGeometry.dispose();
      ringGeometry.dispose();
      renderer.dispose();

      if (sceneRef.current) {
        sceneRef.current.clear();
      }

      frontMaterialRef.current = null;
      backMaterialRef.current = null;
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      coinGroupRef.current = null;
      faceGeometryRef.current = null;
      edgeGeometryRef.current = null;
      flipStateRef.current = { active: false, startTime: 0, from: 0, to: 0 };
    };
  }, [backTextureUrl, playbackUrl, posterUrl]);

  const startPlayback = async () => {
    const video = videoRef.current;
    const frontMaterial = frontMaterialRef.current;
    if (!video || !frontMaterial) return;

    try {
      await video.play();
      if (!videoTextureRef.current) {
        const videoTexture = new THREE.VideoTexture(video);
        videoTexture.colorSpace = THREE.SRGBColorSpace;
        videoTexture.generateMipmaps = false;
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTextureRef.current = videoTexture;
      }
      frontMaterial.map = videoTextureRef.current;
      frontMaterial.needsUpdate = true;
      interactionStepRef.current = 1;
      startedRef.current = true;
      setHasStartedPlayback(true);
      setCurrentFace(FACE_FRONT);
      setIsMuted(Boolean(video.muted));
    } catch (error) {
      reportPlaybackError(error);
    }
  };

  const unmutePlayback = async () => {
    const video = videoRef.current;
    if (!video) return false;
    try {
      video.muted = false;
      await video.play();
      setIsMuted(false);
      return true;
    } catch (error) {
      video.muted = true;
      setIsMuted(true);
      reportPlaybackError(error);
      return false;
    }
  };

  const startFlip = (nextFace) => {
    const coin = coinGroupRef.current;
    if (!coin) return;
    const targetRotation = nextFace === FACE_BACK ? Math.PI : 0;
    flipStateRef.current = {
      active: true,
      startTime: performance.now(),
      from: coin.rotation.y,
      to: targetRotation,
    };
    interactionStepRef.current = nextFace === FACE_BACK ? 2 : 1;
    faceRef.current = nextFace;
    setCurrentFace(nextFace);
    setIsFlipping(true);
  };

  const handleActivate = async () => {
    if (isFlipping || !isReady) return;

    if (!startedRef.current) {
      await startPlayback();
      if (videoRef.current?.muted) {
        const unmuted = await unmutePlayback();
        if (unmuted) startFlip(FACE_BACK);
      }
      return;
    }

    if (videoRef.current?.muted || isMuted) {
      const unmuted = await unmutePlayback();
      if (unmuted) startFlip(FACE_BACK);
      return;
    }

    if (interactionStepRef.current <= 1) {
      startFlip(FACE_BACK);
      return;
    }

    startFlip(FACE_FRONT);
  };

  return (
    <button
      ref={containerRef}
      type="button"
      className={`${styles.viewer} ${className}`.trim()}
      onClick={handleActivate}
      aria-label={accessibilityLabel}
      disabled={!isReady || !playbackUrl}
      data-face={currentFace}
      data-playing={hasStartedPlayback}
      data-flipping={isFlipping}
      data-reduced-motion={prefersReducedMotion}
    >
      <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
    </button>
  );
}
